import {
  InfoAlert,
  TokenOrUnderlyingYieldBearingAssetDropDownSelectBox,
} from '@/components';
import { useWalletsContext } from '@/components/providers';
import {
  TokenInterface,
  UnderlyingYieldBearingAssetInterface,
  WalletTokenInterface,
  WalletUnderlyingYieldBearingAssetInterface,
} from '@/interfaces';
import { logInfo, logWarning } from '@/utils/logger';
import { TRADE_CARD } from '@/utils/strings';
import React, { FC, useCallback, useMemo } from 'react';
import {
  FieldErrors,
  UseFormClearErrors,
  UseFormRegister,
  UseFormSetValue,
} from 'react-hook-form';
import { BuyableAssetType } from './interfaces/TradeInterfaces';
type SellableAssetType =
  | WalletTokenInterface
  | WalletUnderlyingYieldBearingAssetInterface;
interface Props {
  selectedAsset: SellableAssetType | null;
  handleSelectChange: (asset: SellableAssetType | null) => void;
  register?: UseFormRegister<any>;
  setValue?: UseFormSetValue<any>;
  clearErrors?: UseFormClearErrors<any>;
  errors?: FieldErrors;
  buyAsset: BuyableAssetType | null;
}
const SellWalletTokenOrUnderlyingYieldBearingAssetDropDownSelectBox: FC<
  Props
> = ({
  selectedAsset,
  handleSelectChange,
  register,
  setValue,
  clearErrors,
  errors,
  buyAsset,
}) => {
  const {
    getTokensWithPositiveBalance,
    getUnderlyingYieldBearingAssetsWithPositiveBalance,
  } = useWalletsContext();
  const assetsToDisplay = useMemo(() => {
    const tokens = getTokensWithPositiveBalance();
    const yieldBearingAssets =
      getUnderlyingYieldBearingAssetsWithPositiveBalance();
    logInfo('Assets retrieved for dropdown', 'SellWalletTokenDropdown', {
      tokensCount: tokens.length,
      yieldBearingAssetsCount: yieldBearingAssets.length,
    });
    let allAssets = [...tokens, ...yieldBearingAssets];
    if (buyAsset && buyAsset.id) {
      allAssets = allAssets.filter(
        (asset) => asset.id.toLowerCase() !== buyAsset.id.toLowerCase(),
      );
    }
    return allAssets;
  }, [
    getTokensWithPositiveBalance,
    getUnderlyingYieldBearingAssetsWithPositiveBalance,
    buyAsset,
  ]);
  const handleAssetSelect = useCallback(
    (
      asset:
        | TokenInterface
        | UnderlyingYieldBearingAssetInterface
        | WalletTokenInterface
        | WalletUnderlyingYieldBearingAssetInterface,
    ) => {
      const completeAsset = assetsToDisplay.find((a) => a.id === asset.id);
      if (!completeAsset) {
        logWarning(
          'Asset not found in display list',
          'SellWalletTokenDropdown',
          {
            assetId: asset.id,
            assetSymbol: asset.symbol,
          },
        );
        return;
      }
      if ('balance' in completeAsset && 'balanceInUsd' in completeAsset) {
        logInfo('Valid asset selected', 'SellWalletTokenDropdown', {
          assetId: completeAsset.id,
          assetSymbol: completeAsset.symbol,
          blockchain: completeAsset.blockchain,
        });
        handleSelectChange(
          completeAsset as
            | WalletTokenInterface
            | WalletUnderlyingYieldBearingAssetInterface,
        );
      } else {
        logWarning(
          'Invalid asset type - missing required properties',
          'SellWalletTokenDropdown',
          {
            assetId: asset.id,
            hasBalance: 'balance' in completeAsset,
            hasBalanceInUsd: 'balanceInUsd' in completeAsset,
          },
        );
      }
    },
    [handleSelectChange, assetsToDisplay],
  );
  return (
    <TokenOrUnderlyingYieldBearingAssetDropDownSelectBox
      name="sellAsset"
      label={TRADE_CARD.SCREENS.TRADE.LABELS.SELL_ASSET_SELECTION_FIELD.LABEL}
      assetsToDisplay={assetsToDisplay}
      selectedAsset={selectedAsset}
      onAssetSelect={handleAssetSelect}
      register={register}
      setValue={setValue}
      clearErrors={clearErrors}
      errors={errors}
      showNetworkForSelected={true}
      showBalanceForSelected={true}
      showSubNetworkNextToMainNetwork={true}
      tooltip={
        <InfoAlert
          title={
            TRADE_CARD.SCREENS.TRADE.LABELS.SELL_ASSET_SELECTION_FIELD
              .TOOLTIP_TITLE
          }
          description={
            TRADE_CARD.SCREENS.TRADE.LABELS.SELL_ASSET_SELECTION_FIELD
              .TOOLTIP_DESCRIPTION
          }
          intent="transparent"
        />
      }
      tooltipIsFullBleed
    />
  );
};
export default React.memo(
  SellWalletTokenOrUnderlyingYieldBearingAssetDropDownSelectBox,
);
